<template>
  <div class="cockpit-page" data-testid="user-projects-cockpit-view">
    <div class="mb-4">
      <h1 class="text-[22px] font-bold text-[var(--color-dark)] m-0 leading-tight">
        {{ $t('UserProjects.cockpit.pageTitle') }}
      </h1>
      <p class="text-[13px] text-[var(--color-disabled)] mt-0.5 mb-0">
        {{ pageSubline }}
      </p>
    </div>

    <Message v-if="!selectedProjectId && userProjectContextReady" severity="info" :closable="false">
      {{ $t('UserProjects.context.required') }}
    </Message>

    <Message v-if="error" severity="error" :closable="false">
      {{ error }}
    </Message>

    <div
      v-if="inactiveUsersExcluded > 0 && isFullView"
      class="flex items-center gap-1.5 text-[12px] text-[#ABABAB] mb-3"
      data-testid="cockpit-inactive-note"
    >
      <i class="pi pi-info-circle text-[0.85rem]" aria-hidden="true" />
      <span>
        {{ $t('UserProjects.cockpit.inactiveExcluded', { count: inactiveUsersExcluded }) }}
      </span>
    </div>

    <div
      v-if="showTenantBadge"
      class="mb-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium"
      style="background-color: #FDF0E6; color: #e4821a"
      data-testid="cockpit-tenant-badge"
    >
      <i class="pi pi-info-circle text-[0.75rem]" aria-hidden="true" />
      {{ $t('UserProjects.cockpit.tenantView') }}
    </div>

    <div v-if="loading && !ppcData" class="text-sm text-[var(--color-disabled)] py-8">
      {{ $t('UserProjects.cockpit.loading') }}
    </div>

    <template v-else-if="selectedProjectId && userProjectContextReady">
      <CockpitKpiSection
        :participant-mode="!isFullView"
        :current="current"
        :history="history"
        :average-ppc="averagePpc"
        :ppc-period="ppcPeriod"
        :thresholds="thresholds"
        :milestone-chips="milestoneChips"
        :open-constraints-total="openConstraintsTotal"
        @open-ppc-drawer="openPpcDrawer"
        @go-milestones="goMilestones"
        @go-constraints="goConstraints"
      />

      <template v-if="isFullView">
        <CockpitPpcTrendSection
          :ppc-level="ppcLevel"
          :ppc-period="ppcPeriod"
          :ppc-entity-id="ppcEntityId"
          :teams="teams"
          :persons="persons"
          :chart-bars="chartBars"
          @update:ppc-level="setPpcLevel"
          @update:ppc-period="setPpcPeriod"
          @update:ppc-entity-id="setPpcEntityId"
        />

        <CockpitCqiSection
          :current="cqiCurrent"
          :chart-bars="cqiChartBars"
          :thresholds="cqiThresholds"
        />

        <CockpitTbiSection
          :current="tbiCurrent"
          :chart-bars="tbiChartBars"
          :thresholds="tbiThresholds"
        />

        <CockpitCommitmentQualitySection
          :loading="cqLoading"
          :error="cqError"
          :insufficient-data="cqInsufficient"
          :reliability-percent="cqReliability"
          :ownership-percent="cqOwnership"
          :metrics="cqMetrics"
        />

        <CockpitMilestoneList
          :milestones="enrichedMilestones"
          @view-all="goMilestones"
        />

        <CockpitOpenConstraintsList
          :constraints="openConstraints"
          :summary="constraintsSummary"
          @view-all="goConstraints"
        />

        <CockpitImpactAnalysisLink />
      </template>
    </template>

    <CockpitPpcDetailDrawer
      :open="drawerOpen"
      :loading="breakdownLoading"
      :breakdown="breakdown"
      @close="closePpcDrawer"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Message from 'primevue/message';
import CockpitKpiSection from '@/components/CustomerApp/UserProjects/Cockpit/CockpitKpiSection.vue';
import CockpitPpcTrendSection from '@/components/CustomerApp/UserProjects/Cockpit/CockpitPpcTrendSection.vue';
import CockpitCqiSection from '@/components/CustomerApp/UserProjects/Cockpit/CockpitCqiSection.vue';
import CockpitTbiSection from '@/components/CustomerApp/UserProjects/Cockpit/CockpitTbiSection.vue';
import CockpitCommitmentQualitySection from '@/components/CustomerApp/UserProjects/Cockpit/CockpitCommitmentQualitySection.vue';
import CockpitPpcDetailDrawer from '@/components/CustomerApp/UserProjects/Cockpit/CockpitPpcDetailDrawer.vue';
import CockpitMilestoneList from '@/components/CustomerApp/UserProjects/Cockpit/CockpitMilestoneList.vue';
import CockpitOpenConstraintsList from '@/components/CustomerApp/UserProjects/Cockpit/CockpitOpenConstraintsList.vue';
import CockpitImpactAnalysisLink from '@/components/CustomerApp/UserProjects/Cockpit/CockpitImpactAnalysisLink.vue';
import { userIsTenantAdmin } from '@/utils/userRoles';

export default {
  name: 'UserProjectsCockpitView',
  components: {
    Message,
    CockpitKpiSection,
    CockpitPpcTrendSection,
    CockpitCqiSection,
    CockpitTbiSection,
    CockpitCommitmentQualitySection,
    CockpitPpcDetailDrawer,
    CockpitMilestoneList,
    CockpitOpenConstraintsList,
    CockpitImpactAnalysisLink,
  },
  computed: {
    ...mapGetters('pmProject', {
      selectedProjectId: 'selectedProjectId',
      selectedProject: 'selectedProject',
      userProjectContextReady: 'userProjectContextReady',
    }),
    ...mapGetters('pmCockpitPage', [
      'loading',
      'error',
      'ppcLevel',
      'ppcPeriod',
      'ppcEntityId',
      'ppcData',
      'current',
      'history',
      'teams',
      'persons',
      'thresholds',
      'inactiveUsersExcluded',
      'averagePpc',
      'chartBars',
      'drawerOpen',
      'breakdown',
      'breakdownLoading',
      'isFullView',
      'enrichedMilestones',
      'milestoneChips',
      'openConstraints',
      'openConstraintsTotal',
      'constraintsSummary',
    ]),
    ...mapGetters('pmCqi', {
      cqiCurrent: 'current',
      cqiChartBars: 'chartBars',
      cqiThresholds: 'thresholds',
    }),
    ...mapGetters('pmTbi', {
      tbiCurrent: 'current',
      tbiChartBars: 'chartBars',
      tbiThresholds: 'thresholds',
    }),
    ...mapGetters('pmCommitmentQuality', {
      cqLoading: 'loading',
      cqError: 'error',
      cqInsufficient: 'insufficientData',
      cqReliability: 'reliabilityPercent',
      cqOwnership: 'ownershipPercent',
      cqMetrics: 'metrics',
    }),
    ...mapGetters({
      currentUser: 'getUser',
    }),
    pageSubline() {
      const projectName = this.selectedProject?.name || this.$t('UserProjects.context.empty');
      const kw = this.current?.label || '';
      return kw ? `${projectName} · ${kw}` : projectName;
    },
    showTenantBadge() {
      return this.isFullView && userIsTenantAdmin(this.currentUser);
    },
  },
  watch: {
    userProjectContextReady: {
      immediate: true,
      handler(ready) {
        if (ready) this.loadPage();
      },
    },
    selectedProjectId() {
      if (this.userProjectContextReady) this.loadPage();
    },
  },
  methods: {
    ...mapActions('pmCockpitPage', [
      'loadPage',
      'setPpcLevel',
      'setPpcPeriod',
      'setPpcEntityId',
      'openPpcDrawer',
      'closePpcDrawer',
    ]),
    goMilestones() {
      this.$router.push('/user-projects/milestones');
    },
    goConstraints() {
      this.$router.push('/user-projects/constraints');
    },
  },
};
</script>
